import React, { useEffect } from "react";
import ProductLineGate from "./product-line-gate";
import PropTypes from "prop-types";
import ChooseSupplies from "./choose-supplies";
import FormElement from "./form-elements";
import { Formik, Field, Form } from "formik";
import Dysfunctions from "./dysfunctions";
import PhysicianForm from "./physician";
import InsuranceForm from "./insurance";
import AddressForm from "./address";
import { RadioGroupField } from "components/form";
import getInitialValues from "./initial-values";
import OrderFormValidationSchema from "./validation";
import { OrderFormContext } from "./provider";
import { Spinner } from "components/ui";
import { Button } from "@mui/material";
import "./styles.scss";

const OrderForm = () => {
  const {
    patientId,
    patientInfo,
    productLine,
    eligibleSupplies,
    supplyPeriodOptions,
    insurance_companies,
    questions,
    last_order,
    order_create_method,
    submitOrderAPI,
    incompleteOrderTrackingAPI
  } = React.useContext(OrderFormContext);

  useEffect(() => {
    incompleteOrderTrackingAPI.fetchData({
      patient_login_id: patientId,
      order_create_method: order_create_method
    });
  }, []);
  const [selectedProductLine, setProductLine] = React.useState(productLine);
  const [formikRef, setFormikRef] = React.useState(null);

  // Trigger validation when formik is ready
  React.useEffect(() => {
    if (formikRef?.validateForm) {
      formikRef.validateForm();
    }
  }, [formikRef]);

  // If productLine is not set, gate on product line selection
  if (!productLine) {
    return (
      <ProductLineGate
        patientInfo={patientInfo}
        setProductLine={setProductLine}
      >
        {/* Will render form after selection */}
      </ProductLineGate>
    );
  }

  // Filter questions, etc. based on selectedProductLine if needed
  var groupedQuestions =
    questions?.reduce((acc, question) => {
      let groupKey = question.category;
      if (groupKey.includes("Dysfunctions|")) {
        const productType = question.category.split("|")[1] ?? "";
        return {
          ...acc,
          Dysfunctions: [
            ...(acc?.Dysfunctions ?? []),
            { ...question, productType }
          ]
        };
      }

      if (!acc[groupKey]) {
        acc[groupKey] = [{ ...question }];
      } else {
        acc[groupKey] = [...acc[groupKey], { ...question }];
      }
      return acc;
    }, {}) ?? {};

  const validationSchema = OrderFormValidationSchema(groupedQuestions);

  const initialValues = getInitialValues({
    patientId,
    eligibleSupplies,
    supplyPeriodOptions,
    productLine: selectedProductLine,
    patientInfo,
    last_order,
    questions
  });

  return (
    <Formik
      initialValues={{
        ...initialValues,
        order_create_method: order_create_method ?? "Patient Portal"
      }}
      initialTouched={{
        questions: Object.keys(groupedQuestions).reduce((acc, category) => {
          groupedQuestions[category].forEach(question => {
            acc[question.id] = true;
          });
          return acc;
        }, {}),
        supply_period: true,
        address_change_type: true
      }}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {formikProps => {
        const { values, isSubmitting, validateForm, setSubmitting } =
          formikProps;

        // Store formik reference for validation trigger
        if (!formikRef) {
          setFormikRef(formikProps);
        }

        const handleCustomSubmit = async () => {
          // Prevent double-submission
          if (isSubmitting) return;

          try {
            setSubmitting(true);

            const errors = await validateForm();

            if (Object.keys(errors).length === 0) {
              await submitOrderAPI.fetchData(values);
            } else {
              console.warn("Validation failed:", errors);
              // Could show toast notification or scroll to first error
            }
          } catch (error) {
            console.error("Submit error:", error);
          } finally {
            setSubmitting(false);
          }
        };

        return (
          <Form
            style={{ minHeight: 300, padding: 25 }}
            className="order-form-new"
            onSubmit={e => {
              e.preventDefault();
              handleCustomSubmit();
            }}
          >
            <ChooseSupplies />
            <hr />
            <h4 style={{ paddingBottom: 15 }}>
              Please Review The Following Information
            </h4>
            <div>
              {groupedQuestions.Usage?.map(x => (
                <FormElement key={x.id} {...x} />
              ))}
            </div>
            <div>
              {groupedQuestions["Additional Info"]?.map(x => (
                <FormElement key={x.id} {...x} />
              ))}
            </div>
            <Dysfunctions questions={groupedQuestions} />
            <div>
              {groupedQuestions["Issues"]?.map(x => (
                <FormElement key={x.id} {...x} />
              ))}
            </div>
            <hr />
            <PhysicianForm
              physician={(() => {
                // Find physician from patient_product_lines by matching product line text
                const productLineEntry = patientInfo?.patient_product_lines?.find(
                  entry => entry.text === selectedProductLine
                );
                
                if (productLineEntry?.physician) {
                  const physician = productLineEntry.physician;
                  return {
                    npi: physician.npi || "",
                    name: physician.name || "",
                    address: {
                      street_address: physician.address?.street_address || "",
                      street_address2: physician.address?.street_address2 || "",
                      city: physician.address?.city || "",
                      zip: physician.address?.zip || "",
                      state: physician.address?.state || ""
                    },
                    phone: physician.phone || "",
                    fax: physician.fax || ""
                  };
                }
                
                // Return empty physician structure if no product line match found
                return {
                  npi: "",
                  name: "",
                  address: {
                    street_address: "",
                    street_address2: "",
                    city: "",
                    zip: "",
                    state: ""
                  },
                  phone: "",
                  fax: ""
                };
              })()}
              questions={groupedQuestions.Physician ?? []}
            />
            <hr />
            <InsuranceForm
              insuranceOptions={insurance_companies ?? []}
              patientInsurances={patientInfo?.insurances ?? []}
              questions={groupedQuestions.Insurance ?? []}
            />
            <hr />
            <AddressForm
              patientInfo={patientInfo}
              questions={groupedQuestions.Address ?? []}
            />
            <hr />
            {groupedQuestions["Misc"]?.map(x => (
              <FormElement key={x.id} {...x} />
            ))}
            <div style={{ padding: 5, marginBottom: 20 }}>
              <Field
                component={RadioGroupField}
                name="supply_period"
                label="How many days of supplies"
                value={values.supply_period}
                options={supplyPeriodOptions}
              />
            </div>
            {groupedQuestions["Disclaimers"]?.map(x => (
              <FormElement key={x.id} {...x} />
            ))}
            <div
              style={{
                display: "flex",
                justifyItems: "flex-start",
                alignItems: "flex-end",
                width: "100%"
              }}
            >
              {isSubmitting ? (
                <Spinner />
              ) : (
                <Button type="submit" color="primary" disabled={isSubmitting}>
                  Submit
                </Button>
              )}
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

OrderForm.propTypes = {
  productLine: PropTypes.string,
  itemEligibility: PropTypes.arrayOf(PropTypes.object),
  questions: PropTypes.arrayOf(PropTypes.object),
  supply_options: PropTypes.arrayOf(PropTypes.object),
  supply_period_options: PropTypes.arrayOf(PropTypes.object),
  insurance_companies: PropTypes.arrayOf(PropTypes.object),
  patientInfo: PropTypes.object
};

export default OrderForm;
